import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

const lbl = "text-[9px] uppercase tracking-wider text-text-muted mb-1 block";
const sel = "w-full bg-app border border-border rounded px-2 py-1.5 text-sm";

// Vínculos del empleado: usuario del sistema, RTC y jefe inmediato (13.1).
export function EmployeeLinkFields({ f, setF }) {
  const [opts, setOpts] = useState({ users: [], representatives: [], employees: [] });
  useEffect(() => { api.get("/hr/link-options").then(({ data }) => setOpts(data)).catch(() => {}); }, []);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="emp-links">
      <div>
        <label className={lbl}>Usuario del sistema</label>
        <select value={f.user_id || ""} onChange={(e) => setF({ ...f, user_id: e.target.value || null })} className={sel} data-testid="emp-user-select">
          <option value="">— Sin usuario —</option>
          {opts.users.map((u) => <option key={u.id} value={u.id}>{u.email}{u.name || u.nombre ? ` · ${u.name || u.nombre}` : ""}</option>)}
        </select>
      </div>
      <div>
        <label className={lbl}>RTC vinculado</label>
        <select value={f.rtc_id || ""} onChange={(e) => setF({ ...f, rtc_id: e.target.value || null })} className={sel} data-testid="emp-rtc-select">
          <option value="">— No es RTC —</option>
          {opts.representatives.map((r) => <option key={r.id} value={r.id}>{r.codigo} · {r.nombre}</option>)}
        </select>
      </div>
      <div>
        <label className={lbl}>Jefe inmediato (aprueba ausencias)</label>
        <select value={f.jefe_id || ""} onChange={(e) => setF({ ...f, jefe_id: e.target.value || null })} className={sel} data-testid="emp-jefe-select">
          <option value="">— Sin jefe asignado —</option>
          {opts.employees.filter((x) => x.id !== f.id).map((x) => <option key={x.id} value={x.id}>{x.nombre} ({x.codigo})</option>)}
        </select>
      </div>
    </div>
  );
}

// Cambio de estado (activo / inactivo / suspendido / baja) con fecha y motivo.
export function EstadoModal({ employee, onClose, onSaved }) {
  const [f, setF] = useState({ estado: employee.estado === "activo" ? "suspendido" : "activo", fecha: new Date().toISOString().slice(0, 10), motivo: "" });
  const save = async () => {
    if (f.estado !== "activo" && !f.motivo.trim()) return toast.error("El motivo es obligatorio");
    try {
      await api.post(`/hr/employees/${employee.id}/estado`, f);
      toast.success("Estado actualizado");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const m = useModalForm({ value: f, onClose, onSave: save });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-md" data-testid="estado-modal">
        <div className="p-4 border-b border-border flex justify-between items-center">
          <h3 className="font-heading text-lg">Cambiar estado — {employee.nombre}</h3>
          <BotonCerrar onClick={m.cerrar} />
        </div>
        <div className="p-4 space-y-3">
          <div className="text-xs text-text-muted">Estado actual: <b className="uppercase">{employee.estado}</b></div>
          <div>
            <label className={lbl}>Nuevo estado</label>
            <select value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })} className={sel} data-testid="estado-select">
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
              <option value="suspendido">Suspendido</option>
              <option value="baja">Baja</option>
            </select>
          </div>
          <div>
            <label className={lbl}>Fecha efectiva</label>
            <input type="date" value={f.fecha} onChange={(e) => setF({ ...f, fecha: e.target.value })} className={sel} />
          </div>
          <div>
            <label className={lbl}>Motivo {f.estado !== "activo" && <span className="text-red-400">*</span>}</label>
            <textarea rows={3} value={f.motivo} onChange={(e) => setF({ ...f, motivo: e.target.value })} className={sel} data-testid="estado-motivo" />
          </div>
          {(employee.estado_historial || []).length > 0 && (
            <div className="bg-app rounded p-2 max-h-32 overflow-y-auto">
              <div className="text-[9px] uppercase text-text-muted mb-1">Historial</div>
              {[...employee.estado_historial].reverse().map((h, i) => (
                <div key={i} className="text-[11px] text-text-secondary">{h.fecha} · <b className="uppercase">{h.estado}</b> · {h.motivo} <span className="text-text-muted">({h.por})</span></div>
              ))}
            </div>
          )}
        </div>
        <div className="p-4 border-t border-border flex justify-end gap-2">
          <button onClick={m.cerrar} className="px-3 py-1.5 text-sm border border-border rounded">Cancelar</button>
          <button onClick={m.guardar} disabled={m.saving || (f.estado !== "activo" && !f.motivo.trim())} title="El motivo es obligatorio" className="px-4 py-1.5 text-sm bg-agro-green text-black font-medium rounded disabled:opacity-40" data-testid="estado-save">{m.saving ? "Guardando…" : "Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
